import * as React from "react";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type {
  CPGridV2Column,
  CPGridV2FilterOption,
} from "shared/components/shared/CPGridV2";
import "./AdminLinksPageStyles.scss";
import { CPText, Link } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import { Chip } from "@progress/kendo-react-buttons";
import { GridFetchResponse, ValueObject } from "shared/types";
import LinkDetailsModal, { UpsertLinkRecord } from "./LinkDetailsModal";
import { useQuery, useQueryClient } from "react-query";
import http from "shared/http";
import {
  useAgenciesLookupQuery,
  useResourceCategoriesLookupQuery,
} from "data/lookup/lookupQueries";
import useAppState from "shared/state/useAppState";
import { useConfirm } from "shared/components/shared/CPConfirmModal/useCPConfirmModalContext";
import { useDeleteLinkMutation } from "data/resources/link";

interface UseGetLinksForGridQueryOptions {
  title?: string;
  category?: string;
  role?: string;
  tier?: string;
  take?: number;
  skip?: number;
  sortBy?: string;
  ascending?: boolean;
}

const useGetLinksForGridQuery = (params: UseGetLinksForGridQueryOptions) => {
  return useQuery(
    ["links-grid", JSON.stringify(params)],
    async () => {
      const response: GridFetchResponse = await http.fetchAsync(
        "admin/links/search",
        params,
      );
      return response;
    },
    {
      staleTime: 30 * 1000, // Cache for 30 seconds
      enabled: true,
    },
  );
};

// Columns

interface ColumnHandlers {
  onEdit: (link: UpsertLinkRecord) => void;
  onDelete: (link: UpsertLinkRecord) => void;
}

const createColumns = ({
  onEdit,
  onDelete,
}: ColumnHandlers): CPGridV2Column[] => [
  {
    columnType: "menu",
    onMenuOpen: () => [
      { label: "Edit", icon: "fa-solid fa-pencil" },
      { label: "Delete", icon: "fa-solid fa-trash" },
    ],
    onMenuItemClick: (label, dataItem) => {
      if (label === "Edit") onEdit(dataItem as UpsertLinkRecord);
      if (label === "Delete") onDelete(dataItem as UpsertLinkRecord);
    },
  },
  { field: "id", title: "ID", width: 70, hide: true, pinned: true },
  {
    field: "title",
    title: "Title",
    width: 280,
    renderCell: (data: ValueObject) => (
      <CPText noWrap style={{ textTransform: "capitalize" }}>
        {data.dataItem.title}
      </CPText>
    ),
  },
  {
    field: "url",
    title: "URL",
    width: 280,
    renderCell: (data: ValueObject) => {
      const { url, mode } = data.dataItem;
      if (mode == "WINDOW") {
        return (
          <Link
            as="button"
            onClick={() =>
              window.open(
                url,
                "_blank",
                "noopener,noreferrer,width=1200,height=800",
              )
            }
            style={{ fontSize: 14 }}
          >
            {url}
          </Link>
        );
      }
      return (
        <Link
          url={url}
          target={mode == "TAB" ? "_blank" : "_self"}
          style={{ fontSize: 14 }}
        >
          {url}
        </Link>
      );
    },
  },
  {
    field: "isActive",
    title: "Active",
    width: 100,
    renderCell: (data: ValueObject) => (
      <Chip
        text={data.dataItem.isActive ? "Active" : "Inactive"}
        themeColor={data.dataItem.isActive ? "success" : "error"}
        size="small"
      />
    ),
  },
  {
    field: "resourceTier",
    title: "Tier",
    width: 110,
    renderCell: (data: ValueObject) => {
      const tier: string = data.dataItem.resourceTier;
      if (!tier) return null;
      const tierMap: Record<string, { color: string; bg: string }> = {
        BRONZE: { color: "#b5651d", bg: "#fdf3e7" },
        SILVER: { color: "#546e7a", bg: "#eceff1" },
        GOLD: { color: "#b8860b", bg: "#fffde7" },
      };
      const s = tierMap[tier.toUpperCase()] ?? {
        color: "#64748b",
        bg: "#f1f5f9",
      };
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            borderRadius: 10,
            fontSize: "0.73rem",
            fontWeight: 600,
            backgroundColor: s.bg,
            color: s.color,
            border: `1px solid ${s.color}40`,
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: s.color,
              flexShrink: 0,
            }}
          />
          {tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase()}
        </span>
      );
    },
  },
  {
    field: "categories",
    title: "Categories",
    width: 220,
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.categories ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "agencies",
    title: "Agencies",
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.agencies ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "roles",
    title: "Roles",
    sortable: false,
    renderCell: (data: ValueObject) => {
      const items: string[] = data.dataItem.roles ?? [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
];

const AdminLinksPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { setToastMsg } = useAppState();
  const confirm = useConfirm();
  const { mutate: deleteLink, isLoading: isDeleting } = useDeleteLinkMutation();
  const [filterParams, setFilterParams] =
    React.useState<UseGetLinksForGridQueryOptions>({ take: 20, skip: 0 });
  const { data: categories, isLoading: isCategoriesLoading } =
    useResourceCategoriesLookupQuery();
  const { data: agencies, isLoading: isAgenciesLoading } =
    useAgenciesLookupQuery();
  const {
    data: links,
    isFetching: isLinksLoading,
    refetch,
  } = useGetLinksForGridQuery(filterParams);

  const [selectedLink, setSelectedLink] =
    React.useState<UpsertLinkRecord | null>(null);

  const handleDeleteLink = (link: UpsertLinkRecord) => {
    confirm((confirmed) => {
      if (confirmed) {
        deleteLink(link.id, {
          onSuccess: () => {
            setToastMsg("Link deleted successfully", "success");
            queryClient.invalidateQueries(["links-grid"]);
          },
          onError: () => {
            setToastMsg("Failed to delete link", "error");
          },
        });
      }
    }, `Are you sure you want to delete "${link.title}"?`);
  };

  const adapter = useCPGridV2({
    columns: createColumns({
      onEdit: setSelectedLink,
      onDelete: handleDeleteLink,
    }),
    data: links?.records ?? [],
    showLoader: isLinksLoading,
    totalRecords: links?.totalRecords ?? 0,
  });

  const filterOptions = React.useMemo<CPGridV2FilterOption[]>(() => {
    return [
      {
        filterType: "text",
        label: "Title",
        columnKey: "title",
        placeholder: "e.g. Mastery Console",
      },
      {
        filterType: "dropdown",
        label: "Category",
        columnKey: "categories",
        options:
          categories?.map((category) => ({
            label: category,
            value: category,
          })) ?? [],
      },
      {
        filterType: "multicheck",
        label: "Agency",
        columnKey: "agencies",
        toType: "array",
        options:
          agencies?.map((x) => ({
            label: x.agency,
            value: x.agency,
          })) ?? [],
      },
      {
        filterType: "dropdown",
        label: "Active",
        columnKey: "isActive",
        options: [
          { label: "Active", value: true },
          { label: "Inactive", value: false },
        ],
      },
      {
        filterType: "dropdown",
        label: "Tier",
        columnKey: "resourceTier",
        options: [
          { label: "Bronze", value: "BRONZE" },
          { label: "Silver", value: "SILVER" },
          { label: "Gold", value: "GOLD" },
        ],
      },
    ];
  }, [categories, agencies]);

  return (
    <div className="admin-links-page">
      <CPGridV2
        name="admin-links-grid"
        title="Link Management"
        adapter={adapter}
        compact
        sortable
        pageable
        noWrap
        gridLines="rows"
        filterOptions={filterOptions}
        onApplyFilters={(filters, paging) => {
          setFilterParams({
            ...filters,
            ...paging,
          } as UseGetLinksForGridQueryOptions);
        }}
        onClearFilters={() => setFilterParams({ take: 20, skip: 0 })}
        onRefresh={() => queryClient.invalidateQueries(["links-grid"])}
        renderActions={() => (
          <CPButton
            fillMode="flat"
            size="small"
            onClick={() => setSelectedLink(new UpsertLinkRecord())}
          >
            + Add Link
          </CPButton>
        )}
      />

      <LinkDetailsModal
        key={selectedLink?.id}
        selectedLink={selectedLink}
        onClose={() => {
          setSelectedLink(null);
        }}
        onSave={() => {
          setSelectedLink(null);
          queryClient.invalidateQueries(["links-grid"]);
        }}
        onDelete={() => {
          setSelectedLink(null);
          queryClient.invalidateQueries(["links-grid"]);
        }}
      />
    </div>
  );
};

export { AdminLinksPage };
export default AdminLinksPage;
